/**
 * Preset chess positions for the teaching board
 * These are common opening positions and patterns that teachers can quickly load
 */

export interface TeachingPresetPosition {
    id: string;
    name: string;
    description: string;
    fen: string;
    category: 'opening' | 'pattern' | 'tactical' | 'endgame';
}

export const TEACHING_PRESET_POSITIONS: TeachingPresetPosition[] = [
    // Openings - Sicilian Defense variations
    {
        id: 'sicilian-dragon',
        name: 'Sicilian Defense - Dragon Variation',
        description: 'Classic Sicilian Defense with Dragon setup. Black fianchettoes the dark-squared bishop.',
        fen: 'rnbqkb1r/pp2pp1p/3p1np1/8/3NP3/2N5/PPP2PPP/R1BQKB1R w KQkq - 0 6',
        category: 'opening'
    },
    {
        id: 'sicilian-najdorf',
        name: 'Sicilian Defense - Najdorf Variation',
        description: 'Popular Najdorf line with ...a6 and ...e5. One of the most analyzed openings in chess.',
        fen: 'rnbqkb1r/1p2pppp/p2p1n2/8/3NP3/2N5/PPP2PPP/R1BQKB1R w KQkq - 0 6',
        category: 'opening'
    },
    {
        id: 'sicilian-scheveningen',
        name: 'Sicilian Defense - Scheveningen Variation',
        description: 'Solid Scheveningen structure with ...e6. Leads to complex positions.',
        fen: 'rnbqkb1r/pp3ppp/3ppn2/8/3NP3/2N5/PPP2PPP/R1BQKB1R w KQkq - 0 6',
        category: 'opening'
    },
    
    // Openings - Other popular defenses
    {
        id: 'french-defense',
        name: 'French Defense',
        description: 'Classic French Defense with ...e6. Creates a solid pawn structure and closed center.',
        fen: 'rnbqkb1r/pppp1ppp/4pn2/8/3PP3/5N2/PPP2PPP/RNBQKB1R b KQkq - 0 3',
        category: 'opening'
    },
    {
        id: 'italian-game',
        name: 'Italian Game',
        description: 'Classical Italian Game with Bc4. Leads to sharp, tactical play.',
        fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 4 4',
        category: 'opening'
    },
    {
        id: 'ruy-lopez',
        name: 'Ruy Lopez (Spanish Opening)',
        description: 'The most popular opening after 1.e4 e5. Named after a 16th-century Spanish priest.',
        fen: 'r1bqkb1r/pppp1ppp/2n2n2/1B2p3/4P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 3 3',
        category: 'opening'
    },
    {
        id: 'queens-gambit',
        name: 'Queen\'s Gambit',
        description: 'The most popular opening starting with 1.d4. Offers a pawn for central control.',
        fen: 'rnbqkb1r/ppp1pppp/5n2/3p4/2PP4/8/PP2PPPP/RNBQKBNR b KQkq c3 0 3',
        category: 'opening'
    },
    {
        id: 'kings-indian',
        name: 'King\'s Indian Defense',
        description: 'Hypermodern opening with ...Nf6, ...g6, and ...Bg7. Creates dynamic counterplay.',
        fen: 'rnbqkb1r/pppppp1p/5np1/8/2PP4/8/PP2PPPP/RNBQKBNR w KQkq - 0 3',
        category: 'opening'
    },
    {
        id: 'caro-kann',
        name: 'Caro-Kann Defense',
        description: 'Solid defensive opening with ...c6. Creates strong pawn structure for Black.',
        fen: 'rnbqkb1r/pp1ppppp/2p2n2/8/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 0 3',
        category: 'opening'
    },
    {
        id: 'english-opening',
        name: 'English Opening',
        description: 'Hypermodern opening starting with 1.c4. Controls the center from the flank.',
        fen: 'rnbqkbnr/pppppppp/8/8/2P5/8/PP1PPPPP/RNBQKBNR b KQkq c3 0 1',
        category: 'opening'
    },
    
    // Tactical Patterns
    {
        id: 'back-rank-mate',
        name: 'Back Rank Mate Pattern',
        description: 'Classic back rank weakness pattern. The king is trapped behind its own pawns.',
        fen: 'r3k2r/ppp2ppp/2n2n2/2bqp3/2BpP3/3P1N2/PPP2PPP/R2QK2R w KQkq - 0 8',
        category: 'pattern'
    },
    {
        id: 'greek-gift',
        name: 'Greek Gift Sacrifice',
        description: 'Classic bishop sacrifice on h7 (or h2) to open up the king\'s position.',
        fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p2Q/2B1P3/5N2/PPPP1PPP/RNB1K2R b KQkq - 3 4',
        category: 'tactical'
    },
    
    // Endgame Patterns
    {
        id: 'rook-endgame',
        name: 'Rook Endgame - Lucena Position',
        description: 'Classic rook endgame technique for promoting a pawn with the defending king cut off.',
        fen: '8/8/8/8/4K3/4P3/5k2/4R3 w - - 0 1',
        category: 'endgame'
    },
    {
        id: 'opposition',
        name: 'King and Pawn - Opposition',
        description: 'The concept of opposition in king and pawn endgames. A fundamental endgame technique.',
        fen: '8/8/8/8/4k3/4P3/4K3/8 w - - 0 1',
        category: 'endgame'
    }
];

